import { consumeQuickConnectIntent } from "features/onboarding/guided/quickConnectIntent";
import { useOnboardingAnalytics } from "analytics/onboarding";
import {
  getConnectContext,
  toErrorMessage,
  useAccountSetupAnalytics,
} from "analytics/accountSetup";
import { useSelectRealUser } from "@shared/utils/hooks/misc";
import { AddClientResponse } from "@shared/types";
import {
  FC,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { openMessage } from "utils/misc";
import Link from "common/Typography/Link";
import Text from "common/Typography/Text";
import styled, { useTheme } from "styled-components";
import NewLookFormAlert from "features/portfolio/CreateAccount/NewLook/NewLookFormAlert";
import { QuickConnectContext } from "@shared/features/onboarding/QuickConnect.context";
import useQuery from "utils/hooks/useQuery";
import {
  CREATE_ACCOUNT_PATH,
  LOCAL_STORAGE_TELEGRAM_QUICK_CONNECT_KEY,
  PORTFOLIO_ACCOUNTS_PATH,
} from "common/constants";
import { useHistory, useRouteMatch } from "react-router-dom";
import useQuickConnectProvider from "@shared/features/onboarding/hooks/useQuickConnectProvider";
import { BINANCE_FUTURES_LINK } from "@shared/common/constants";
import { getRouteWithExchange } from "utils/routes";
import TgQuickConnectSuccessModal from "features/configuration/accounts/CreateAccount/TgQuickConnectSuccessModal";
import { usePollClientsListData } from "utils/hooks/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants";
import { telegramOpenLink } from "features/telegram/utils";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery.ts";
import {
  QUICK_CONNECT_CONN_CODE_QUERY_PARAM,
  QUICK_CONNECT_STATE_QUERY_PARAM,
} from "features/configuration/common/constants";

interface QuickConnectWrapperProps {
  onSubmitSuccess?: () => void;
  isOnboarding?: boolean;
  getSuccessMessage?: (
    response?: AddClientResponse,
    exchangesList?: string[]
  ) => void;
  getErrorNotification?: (description: string, title?: string) => void;
  onCreateAccessToken?: (primaryExchange: string) => void;
}

const QuickConnectProvider: FC<PropsWithChildren<QuickConnectWrapperProps>> = ({
  children,
  onSubmitSuccess,
  isOnboarding,
  getSuccessMessage,
  getErrorNotification,
  onCreateAccessToken: propsOnCreateAccessToken,
}) => {
  const [tgQuickConnectModalOpen, setTgQuickConnectModalOpen] = useState(false);
  const trackOnboarding = useOnboardingAnalytics();
  const trackAccountSetup = useAccountSetupAnalytics();
  const alreadyConnectedRef = useRef(false);
  const { isNewLook } = useTheme();
  const { startPolling, pollClientsListData } = usePollClientsListData();
  const query = useQuery();
  const realUser = useSelectRealUser();
  const history = useHistory();
  const { getUpdatedPath } = useHistoryWithQuery();
  const queryCode = query.get(QUICK_CONNECT_CONN_CODE_QUERY_PARAM);
  const queryState = query.get(QUICK_CONNECT_STATE_QUERY_PARAM);
  const queryExchanges = query.get("exchanges_list");
  const isPortfolioCreateAccountPath = !!useRouteMatch(CREATE_ACCOUNT_PATH);
  const isPortfolioCreateAccountPathRef = useRef<boolean>(undefined);
  isPortfolioCreateAccountPathRef.current = isPortfolioCreateAccountPath;

  const queryData = {
    statusCode: query.get("status_code"),
    failMessage: query.get("fail_message"),
    accountBaseName: query.get("client_base_name"),
    exchangesList: queryExchanges,
    exchange: query.get("exchange"),
  };

  const connectProperties = {
    context: getConnectContext(isOnboarding),
    exchange: queryData.exchange ?? undefined,
    connect_method: "quick_connect" as const,
  };

  // A failed callback without a message shows a generic error, not the
  // error notification: record that one here.
  useEffect(() => {
    if (queryData.statusCode !== "400" || queryData.failMessage) return;
    trackAccountSetup("Connect Account - Failed", connectProperties);
    if (isOnboarding)
      trackOnboarding("Step Failed", {
        step: "connect_exchange",
        error_code: "exchange_connection_failed",
        exchange: queryData.exchange ?? undefined,
        connect_method: "quick_connect",
      });
  }, [queryData.statusCode, queryData.failMessage]);

  const onCreateAccessToken = (primaryExchange: string) => {
    history.push(getRouteWithExchange(primaryExchange));
  };

  const trackQuickConnectFailed = (description: string) => {
    trackAccountSetup("Connect Account - Failed", {
      ...connectProperties,
      error_message: toErrorMessage(description),
    });
    if (isOnboarding)
      trackOnboarding("Step Failed", {
        step: "connect_exchange",
        error_code: "exchange_connection_failed",
        exchange: queryData.exchange ?? undefined,
        connect_method: "quick_connect",
      });
  };

  const {
    noFuturesAccountMessageSharedData,
    quickConnectAlreadyUsedMessageSharedData,
    primaryExchange,
    closeDrawer,
    tryAgainLoading,
    isLoading,
    quickConnectLoading,
    onBackToSelectExchange,
    isInitialized,
    quickConnectSucceeded,
    code,
    state,
  } = useQuickConnectProvider({
    openMessage,
    queryCode,
    queryState,
    queryData,
    onInitializeCodeAndState: () => {
      const restoredIntent = isOnboarding
        ? consumeQuickConnectIntent(realUser?.id, queryState)
        : undefined;
      const updatedPath = getUpdatedPath(
        restoredIntent,
        [QUICK_CONNECT_CONN_CODE_QUERY_PARAM, QUICK_CONNECT_STATE_QUERY_PARAM],
        location.pathname
      );
      history.replace(updatedPath);
    },
    isOnboarding,
    onOpenModal: () => history.replace(CREATE_ACCOUNT_PATH),
    onCloseModal: () => {
      if (isPortfolioCreateAccountPathRef.current) {
        history.replace(PORTFOLIO_ACCOUNTS_PATH);
      }
    },
    onSubmitSuccess: () => {
      // An "already connected" answer added no account: told apart in the
      // funnel.
      const already_connected = alreadyConnectedRef.current;
      trackAccountSetup("Connect Account - Connected", {
        ...connectProperties,
        already_connected,
      });
      if (isOnboarding)
        trackOnboarding("Exchange Connected", {
          exchange: queryData.exchange ?? undefined,
          connect_method: "quick_connect",
          already_connected,
        });
      onSubmitSuccess?.();
      const storageValue = localStorage?.getItem(
        LOCAL_STORAGE_TELEGRAM_QUICK_CONNECT_KEY
      );
      if (storageValue) {
        const parsedStartTime = parseInt(storageValue);
        if (performance.now() - parsedStartTime < 1000 * 60 * 10) {
          setTgQuickConnectModalOpen(true);
        }
        localStorage?.removeItem(LOCAL_STORAGE_TELEGRAM_QUICK_CONNECT_KEY);
      }
    },
    getSuccessMessage: (response, exchangesList) => {
      alreadyConnectedRef.current = !!(
        response?.data as { already_connected?: boolean } | undefined
      )?.already_connected;
      getSuccessMessage?.(response, exchangesList);
    },
    getErrorNotification: (description, title) => {
      trackQuickConnectFailed(description);
      getErrorNotification?.(description, title);
    },
    onFixableOnExchangeError: trackQuickConnectFailed,
    onCreateAccessToken: propsOnCreateAccessToken ?? onCreateAccessToken,
    pollingAccountCreated: pollClientsListData?.accountsCreated,
  });

  const getNoFuturesAccountMessageData = () => {
    const data = noFuturesAccountMessageSharedData;
    if (isNewLook) {
      return {
        ...data,
        displayMessage: data.show && (
          <NewLookFormAlert title={data.errorMessage}>
            Navigate to{" "}
            <Link
              href={BINANCE_FUTURES_LINK}
              target="_blank"
              style={{ fontSize: "inherit" }}
            >
              Binance.com/en/futures
            </Link>{" "}
            and create a futures account or unselect the futures sub-exchanges.
          </NewLookFormAlert>
        ),
      };
    }
    return {
      ...data,
      displayMessage: data.show && (
        <MessageWrapper>
          <Text type="danger" style={{ textAlign: "left" }}>
            {data.errorMessage}
          </Text>
          <Text style={{ textAlign: "left" }}>
            Navigate to{" "}
            <Link href={BINANCE_FUTURES_LINK} target="_blank">
              Binance.com/en/futures
            </Link>{" "}
            and create a futures account or unselect the futures sub-exchanges.
          </Text>
        </MessageWrapper>
      ),
    };
  };

  // The exchange allows one Cornix quick-connect key per account and this
  // one already has it (usually from an earlier Cornix account), or Binance
  // wants its API terms accepted first: the user fixes it on the exchange,
  // and coming back here tries again. Only after really leaving (the
  // exchange's page opened, or the page hidden), not when focus just moves
  // into a widget.
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const keyExists = quickConnectAlreadyUsedMessageSharedData;
  const latestKeyExists = useRef(keyExists);
  latestKeyExists.current = keyExists;
  const leftForExchange = useRef(false);
  useEffect(() => {
    leftForExchange.current = false;
    if (!keyExists.show) return;
    const onVisibilityChange = () => {
      if (document.hidden) leftForExchange.current = true;
      else onReturn();
    };
    const onReturn = () => {
      const current = latestKeyExists.current;
      if (document.hidden || !leftForExchange.current || current.loading)
        return;
      leftForExchange.current = false;
      current.onSubmit();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onReturn);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onReturn);
    };
  }, [keyExists.show]);
  const openApiManagement = (event: React.MouseEvent<HTMLAnchorElement>) => {
    leftForExchange.current = true;
    // The Mini App can't follow a new-tab link: Telegram opens it.
    if (IS_TELEGRAM_WEB_APP && telegramOpenLink(keyExists.apiManagementLink))
      event.preventDefault();
  };
  const apiManagementLink = (
    <Link
      href={keyExists.apiManagementLink}
      target="_blank"
      rel="noreferrer"
      onClick={openApiManagement}
      style={{ fontSize: "inherit" }}
    >
      {t("quick_connect.key_exists_api_management", {
        exchange: primaryExchange,
      })}
    </Link>
  );
  // The same steps help when the keys are already in use or there are too
  // many; only the one-key limit gets its own explanation, the others keep
  // the server's.
  const isOneKeyLimit = /only one set of api keys/i.test(
    keyExists.errorMessage ?? ""
  );
  const getKeyExistsTitle = () => {
    if (keyExists.isAPITermsError)
      return t("quick_connect.api_terms_title", { exchange: primaryExchange });
    if (isOneKeyLimit)
      return t("quick_connect.key_exists_title", { exchange: primaryExchange });
    return keyExists.errorMessage;
  };
  const keyExistsTitle = getKeyExistsTitle();
  const comeBackStep = (
    <>
      {t("quick_connect.key_exists_come_back", { exchange: primaryExchange })}
    </>
  );
  const apiTermsSteps = [
    <>
      {t("quick_connect.key_exists_open")} {apiManagementLink}{" "}
      {t("quick_connect.api_terms_main_account", {
        exchange: primaryExchange,
      })}
    </>,
    <>{t("quick_connect.api_terms_accept")}</>,
    comeBackStep,
  ];
  const oneKeySteps = [
    <>
      {t("quick_connect.key_exists_open")} {apiManagementLink}
    </>,
    <>
      {t("quick_connect.key_exists_delete_prefix")} <Bold>Cornix-API</Bold>{" "}
      {t("quick_connect.key_exists_delete_suffix")}
    </>,
    comeBackStep,
  ];
  const keyExistsSteps = keyExists.isAPITermsError
    ? apiTermsSteps
    : oneKeySteps;

  const getQuickConnectAlreadyUsedMessageData = () => {
    const data = keyExists;
    if (isNewLook) {
      return {
        ...data,
        displayMessage: data.show && (
          <NewLookFormAlert title={keyExistsTitle}>
            <NewLookSteps>
              {keyExistsSteps.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </NewLookSteps>
          </NewLookFormAlert>
        ),
      };
    }
    return {
      ...data,
      displayMessage: data.show && (
        <MessageWrapper>
          <Text type="danger" style={{ textAlign: "left" }}>
            {keyExistsTitle}
          </Text>
          <ListWrapper>
            {keyExistsSteps.map((step, index) => (
              <Text key={index}>
                {index + 1}. {step}
              </Text>
            ))}
          </ListWrapper>
        </MessageWrapper>
      ),
    };
  };

  const contextValue = useMemo(() => {
    return {
      noFuturesAccountMessageData: getNoFuturesAccountMessageData(),
      quickConnectAlreadyUsedMessageData:
        getQuickConnectAlreadyUsedMessageData(),
      closeDrawer,
      tryAgainLoading,
      isLoading,
      quickConnectLoading,
      onBackToSelectExchange,
      isInitialized,
      quickConnectSucceeded,
      code,
      state,
      startAccountsPolling: startPolling,
    };
  }, [
    tryAgainLoading,
    isLoading,
    quickConnectLoading,
    quickConnectAlreadyUsedMessageSharedData,
    noFuturesAccountMessageSharedData,
    isInitialized,
    quickConnectSucceeded,
    code,
    state,
  ]);

  return (
    <QuickConnectContext.Provider value={contextValue}>
      {children}
      <TgQuickConnectSuccessModal
        isOpen={tgQuickConnectModalOpen}
        closeModal={() => setTgQuickConnectModalOpen(false)}
      />
    </QuickConnectContext.Provider>
  );
};

export default QuickConnectProvider;

const Bold = styled.span`
  font-weight: ${(p) => p.theme.typography.fontWeights.BOLD};
`;
const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-left: 16px;
  margin-top: 8px;
  border-left: 2px solid ${(p) => p.theme.colors.red};
  text-align: left;
`;
const MessageWrapper = styled.div`
  margin-bottom: 16px;
  align-self: flex-start;
`;
const NewLookSteps = styled.ol`
  margin: 4px 0 0;
  padding-left: 18px;
  li + li {
    margin-top: 2px;
  }
`;
